import { hasAsciiControlOrSpace } from '@/lib/validation';
export interface OperatorConfig {
  authorizeUrl: string;
  tokenUrl: string;
  clientId: string;
  audience: string;
  /** Auth 서버에 등록된 고정 callback. authorize·token 교환 모두 이 값을 보낸다. */
  redirectUri: string;
  /**
   * 이 앱 인스턴스가 callback을 실제로 받는 주소. 등록 origin에서 서빙되면 redirectUri와 같고,
   * 프리뷰 origin이면 redirectUri를 거쳐 중계받은 자기 origin의 같은 경로다.
   */
  callbackUri: string;
  /** 등록 callback이 code/state를 되돌려 보내도 되는 프리뷰 origin 허용 패턴. */
  relayOrigins: string[];
  coreApiUrl: string;
}
export interface OperatorConfigInput {
  authorizeUrl?: string;
  clientId?: string;
  audience?: string;
  redirectUri?: string;
  coreApiUrl?: string;
  /** 앱이 실제로 서빙되는 origin(window.location.origin). 비우면 등록 callback origin으로 본다. */
  appOrigin?: string;
  /** 쉼표·공백으로 구분한 프리뷰 origin 허용 패턴(`https://*.example.pages.dev` 또는 정확한 origin). */
  relayOrigins?: string;
}
export interface OperatorConfigOptions {
  /** Supplied only by Vite's compile-time import.meta.env.DEV. */
  isDevelopment?: boolean;
}

/** 와일드카드 패턴의 고정 접미사는 최소 이 라벨 수여야 한다 — `*.pages.dev` 같은 넓은 허용을 막는다. */
const MIN_RELAY_SUFFIX_LABELS = 3;
const DNS_LABEL = '[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?';

const DEVELOPMENT_CALLBACK_URI = 'http://localhost:3000/auth/callback';
const DEVELOPMENT_CORE_PROXY_URL = '/__api/core/api/v1';

function isRegisteredDevelopmentCallback(url: URL, isDevelopment: boolean) {
  return isDevelopment && url.href === DEVELOPMENT_CALLBACK_URI;
}

/** 허용 패턴 목록을 파싱한다. 형식이 잘못된 항목은 버려서 허용 범위가 넓어지지 않게 한다. */
export function parseRelayOrigins(raw: string | undefined): string[] {
  const patterns: string[] = [];
  for (const item of (raw ?? '').split(/[\s,]+/)) {
    if (!item) continue;
    if (item.includes('*')) {
      // `*`가 들어간 항목은 `https://*.<접미사>` 형태만 허용하고, 어긋나면 정확한 origin으로 취급하지 않고 버린다.
      const suffix = /^https:\/\/\*\.(.+)$/.exec(item)?.[1];
      if (
        suffix &&
        suffix.split('.').length >= MIN_RELAY_SUFFIX_LABELS &&
        new RegExp(`^(?:${DNS_LABEL}\\.)+${DNS_LABEL}$`).test(suffix)
      )
        patterns.push(item);
      continue;
    }
    try {
      const url = new URL(item);
      if (url.protocol === 'https:' && url.origin === item) patterns.push(item);
    } catch {
      /* 잘못된 패턴은 버린다. */
    }
  }
  return patterns;
}

/** origin이 허용 패턴과 일치하는지. 와일드카드의 `*`는 정확히 한 DNS 라벨이다(점·포트·경로 불가). */
export function isAllowedRelayOrigin(origin: string, patterns: readonly string[]): boolean {
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:' || url.origin !== origin) return false;
  return patterns.some((pattern) => {
    if (!pattern.startsWith('https://*.')) return pattern === origin;
    const suffix = pattern.slice('https://*'.length);
    const host = url.host;
    return (
      host.endsWith(suffix) && new RegExp(`^${DNS_LABEL}$`).test(host.slice(0, -suffix.length))
    );
  });
}

/** 공개 등록값만 받는다. 미완성 설정을 제품 OAuth 등록으로 추정하지 않는다. */
export function readOperatorConfig(
  input: OperatorConfigInput,
  { isDevelopment = false }: OperatorConfigOptions = {},
): OperatorConfig | null {
  try {
    const { clientId, audience } = input;
    if (!clientId?.trim() || !audience?.trim()) return null;
    const authorize = new URL(input.authorizeUrl ?? '');
    const redirect = new URL(input.redirectUri ?? '');
    const coreInput = input.coreApiUrl?.trim() ?? '';
    if (!coreInput || coreInput.startsWith('//') || /[\\?#]/.test(coreInput)) return null;
    const core = coreInput.startsWith('/')
      ? new URL(coreInput, redirect.origin)
      : new URL(coreInput);
    if (redirect.href !== input.redirectUri?.trim()) return null;
    if (authorize.protocol !== 'https:') return null;
    if (
      (redirect.protocol !== 'https:' &&
        !isRegisteredDevelopmentCallback(redirect, isDevelopment)) ||
      (core.protocol !== 'https:' &&
        (!isDevelopment ||
          redirect.href !== DEVELOPMENT_CALLBACK_URI ||
          coreInput !== DEVELOPMENT_CORE_PROXY_URL ||
          core.origin !== redirect.origin))
    )
      return null;
    for (const url of [authorize, redirect, core]) {
      if (url.username || url.password || url.search || url.hash) return null;
    }
    if (!authorize.pathname.endsWith('/oauth/authorize')) return null;
    if (!core.pathname.replace(/\/$/, '').endsWith('/api/v1')) return null;
    if (redirect.pathname === '/' || /^\/(properties|dev)(\/|$)/.test(redirect.pathname))
      return null;
    const relayOrigins = parseRelayOrigins(input.relayOrigins);
    const appOrigin = input.appOrigin?.trim();
    // 등록 origin이 아닌 곳에서 서빙되면(프리뷰) 허용 패턴에 든 HTTPS origin일 때만 중계 callback을 쓴다.
    const relayed = !!appOrigin && appOrigin !== redirect.origin;
    if (relayed && !isAllowedRelayOrigin(appOrigin, relayOrigins)) return null;
    const callbackUri = relayed ? `${appOrigin}${redirect.pathname}` : redirect.href;
    const token = new URL(authorize);
    token.pathname = token.pathname.replace(/\/authorize$/, '/token');
    return {
      authorizeUrl: authorize.href,
      tokenUrl: token.href,
      clientId: clientId.trim(),
      audience: audience.trim(),
      redirectUri: redirect.href,
      callbackUri,
      relayOrigins,
      coreApiUrl: core.href.replace(/\/$/, ''),
    };
  } catch {
    return null;
  }
}

/** 복귀는 현재 앱의 업무 경로만 허용한다. callback 데이터나 외부 URL을 재전파하지 않는다. */
export function safeReturnTo(value: string): string {
  const queryStart = value.indexOf('?');
  const routePath = queryStart === -1 ? value : value.slice(0, queryStart);
  const query = queryStart === -1 ? '' : value.slice(queryStart);
  const normalized = `${routePath.endsWith('/') ? routePath.slice(0, -1) : routePath}${query}`;
  if (
    !/^\/(?:(?:properties|contracts|stays)(?:\/[^/?#]+)?|users(?:\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})?|dev(?:\/[^/?#]+)*|access(?:\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})?)(?:\?[^#]*)?$/.test(
      normalized,
    ) ||
    normalized.includes('\\') ||
    hasAsciiControlOrSpace(normalized)
  ) {
    throw new Error('안전하지 않은 복귀 경로입니다.');
  }
  const [path = ''] = normalized.split('?');
  const decoded = decodeURIComponent(path);
  if (
    decoded.includes('%') ||
    decoded.includes('..') ||
    decoded.includes('\\') ||
    decoded.slice(1).includes('//')
  )
    throw new Error('안전하지 않은 복귀 경로입니다.');
  const url = new URL(normalized, 'https://return.invalid');
  if (
    url.searchParams.has('code') ||
    url.searchParams.has('state') ||
    url.searchParams.has('access_token') ||
    url.searchParams.has('refresh_token')
  )
    throw new Error('안전하지 않은 복귀 경로입니다.');
  return normalized;
}
